'use client'

import { ChevronRightIcon } from '@heroicons/react/24/outline'
import { usePathname } from 'next/navigation'

import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { useTopNavV5 } from '@src/components/TopNavV5/TopNavV5Context'
import { TopNavV5Tile } from '@src/components/TopNavV5/TopNavV5Tile'
import { getUnlocalizedPath } from '@src/i18n/locale.util'
import { useTrackSelectNavCta } from '@src/utils/analytics.util'
import { cn } from '@src/utils/tailwind'

export const TopNavV5DesktopSubMenu = () => {
  const pathname = usePathname()
  const trackSelectNavCta = useTrackSelectNavCta()

  const { setExpanded, expandedMenu, leftMenuIndex } = useTopNavV5()

  const subMenu = expandedMenu?.subMenu ?? expandedMenu?.leftMenuItems?.[leftMenuIndex]

  function isCurrentPage(href = '/') {
    try {
      const linkUrl = new URL(href, window.location.origin)
      return linkUrl.pathname === getUnlocalizedPath(pathname)
    } catch (error) {
      return false
    }
  }

  const renderSubItems = () => {
    return subMenu?.items
      ?.filter((subItem) => Boolean(subItem.title))
      .map((subItem, index) => (
        <div key={index}>
          <div className="mb-2 w-fit text-body-md font-medium">
            {subItem.isLink ? (
              <LocalizedLink
                onClick={() => {
                  trackSelectNavCta({ 'cta url': subItem?.link?.href ?? '' })
                  if (isCurrentPage(subItem.link?.href ?? '')) setExpanded(false)
                }}
                className="inline-block hover:underline"
                href={subItem.link?.href ?? ''}
                rel={subItem.link?.newTab ? 'noopener noreferrer' : ''}
                target={subItem.link?.newTab ? '_blank' : '_self'}
              >
                {subItem.title}
              </LocalizedLink>
            ) : (
              <div>{subItem.title}</div>
            )}
          </div>
          <div className="flex flex-col gap-1">
            {subItem.links
              ?.filter((link) => Boolean(link.title))
              .map((link) => (
                <LocalizedLink
                  onClick={() => {
                    trackSelectNavCta({ 'cta url': link?.href })
                    if (isCurrentPage(link.href)) setExpanded(false)
                  }}
                  className="flex w-fit items-center gap-1 rounded-md font-medium text-bodi-gray-butters transition-all hover:text-bodi-white hover:underline"
                  key={`${link.title}-${index}`}
                  href={link.href ?? ''}
                  rel={link.newTab ? 'noopener noreferrer' : ''}
                  target={link.newTab ? '_blank' : '_self'}
                >
                  {link.title}
                  {link.stamp && (
                    <>
                      {' - '}
                      <span className="text-body-sm font-medium text-bodi-green-bright-500">
                        {link.stamp}
                      </span>
                    </>
                  )}
                </LocalizedLink>
              ))}
          </div>
        </div>
      ))
  }

  const renderTiles = () => {
    const tiles = (subMenu?.tiles ?? []).filter((tile) => tile.isVisible ?? true)
    if (tiles.length < 1) return null

    return tiles?.map((tile, i) => <TopNavV5Tile tile={tile} key={i} />)
  }

  const numItems = subMenu?.items?.length ?? 0
  const numTiles = subMenu?.tiles?.length ?? 0

  return (
    <>
      {subMenu?.ctaDesktop?.title && (
        <div className="text-heading-display-3 mb-6 inline-flex items-center gap-2">
          {subMenu?.ctaDesktop?.href ? (
            <LocalizedLink
              className="hover:underline"
              href={subMenu?.ctaDesktop?.href ?? ''}
              rel={subMenu?.ctaDesktop?.newTab ? 'noopener noreferrer' : ''}
              target={subMenu?.ctaDesktop?.newTab ? '_blank' : '_self'}
              onClick={() => {
                trackSelectNavCta({ 'cta url': subMenu?.ctaDesktop?.href })
              }}
            >
              {subMenu?.ctaDesktop?.title}
            </LocalizedLink>
          ) : (
            <div>{subMenu?.ctaDesktop?.title}</div>
          )}
          <ChevronRightIcon className="size-5 text-bodi-green-bright-500" />
        </div>
      )}

      <div className="grid w-full grid-cols-4 gap-10">
        <div
          className={cn('grid grid-cols-subgrid gap-8', {
            'col-span-3': (numTiles === 2 && numItems > 4) || numTiles < 2,
            'col-span-2': numTiles === 2 && numItems <= 4,
            'col-span-4': numTiles === 0,
          })}
        >
          {renderSubItems()}
        </div>

        <div
          className={cn('grid grid-cols-subgrid gap-8', {
            'col-span-2': numTiles === 2 && numItems <= 4,
            hidden: numTiles === 0,
          })}
        >
          {renderTiles()}
        </div>
      </div>
    </>
  )
}
